import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  Timestamp,
  addDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

const NEWSLETTER_COLLECTION = 'newsletterCampaigns';

/**
 * Get newsletter campaigns for a specific user
 * @param {string} userId - The user's UID
 * @returns {Promise<Array>} Array of newsletter campaign objects
 */
export const getUserNewsletterCampaigns = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const campaignsRef = collection(db, NEWSLETTER_COLLECTION);
    const q = query(
      campaignsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const campaigns = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Convert Firestore Timestamps to JavaScript Dates
      createdAt: doc.data().createdAt?.toDate() || null,
      updatedAt: doc.data().updatedAt?.toDate() || null
    }));

    return campaigns;
  } catch (error) {
    console.error('Error getting user newsletter campaigns:', error);
    throw error;
  }
};

/**
 * Add a new newsletter campaign for a specific user
 * @param {string} userId - The user's UID
 * @param {Object} campaignData - Campaign data object
 * @returns {Promise<Object>} Created campaign object
 */
export const addNewsletterCampaign = async (userId, campaignData) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to add campaigns');
    }

    const campaignsRef = collection(db, NEWSLETTER_COLLECTION);
    const newCampaign = {
      ...campaignData,
      userId: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      metrics: {
        openRate: 0,
        clickRate: 0,
        bounceRate: 0,
        unsubscribeRate: 0,
        revenue: 0
      }
    };

    const docRef = await addDoc(campaignsRef, newCampaign);
    console.log('addNewsletterCampaign - Created campaign with ID:', docRef.id);
    
    return {
      id: docRef.id,
      ...newCampaign,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error adding newsletter campaign:', error);
    throw error;
  }
};

/**
 * Update an existing newsletter campaign
 * @param {string} campaignId - The campaign ID
 * @param {Object} updateData - Updated campaign data
 * @returns {Promise<Object>} Updated campaign object
 */
export const updateNewsletterCampaign = async (campaignId, updateData, userId = null) => {
  try {
    console.log('updateNewsletterCampaign called with campaignId:', campaignId);
    console.log('updateNewsletterCampaign called with userId:', userId);
    
    if (!campaignId) {
      throw new Error('Campaign ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update campaigns');
    }

    const campaignRef = doc(db, NEWSLETTER_COLLECTION, campaignId);
    console.log('updateNewsletterCampaign - campaignRef path:', campaignRef.path);
    
    // Check if campaign exists and belongs to current user
    const campaignDoc = await getDoc(campaignRef);
    console.log('updateNewsletterCampaign - campaignDoc.exists():', campaignDoc.exists());
    if (!campaignDoc.exists()) {
      throw new Error('Campaign not found');
    }

    // Use provided userId or fall back to current user
    const targetUserId = userId || auth.currentUser.uid;
    if (campaignDoc.data().userId !== targetUserId) {
      throw new Error('You can only update campaigns for the specified user');
    }

    const updatePayload = {
      ...updateData,
      updatedAt: serverTimestamp()
    };

    await updateDoc(campaignRef, updatePayload);
    
    return {
      id: campaignId,
      ...campaignDoc.data(),
      ...updateData,
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error updating newsletter campaign:', error);
    throw error;
  }
};

/**
 * Delete a newsletter campaign
 * @param {string} campaignId - The campaign ID
 * @returns {Promise<void>}
 */
export const deleteNewsletterCampaign = async (campaignId, userId = null) => {
  try {
    if (!campaignId) {
      throw new Error('Campaign ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to delete campaigns');
    }

    const campaignRef = doc(db, NEWSLETTER_COLLECTION, campaignId);
    
    // Check if campaign exists and belongs to current user
    const campaignDoc = await getDoc(campaignRef);
    if (!campaignDoc.exists()) {
      throw new Error('Campaign not found');
    }

    // Use provided userId or fall back to current user
    const targetUserId = userId || auth.currentUser.uid;
    if (campaignDoc.data().userId !== targetUserId) {
      throw new Error('You can only delete campaigns for the specified user');
    }

    await deleteDoc(campaignRef);
  } catch (error) {
    console.error('Error deleting newsletter campaign:', error);
    throw error;
  }
};

/**
 * Get newsletter campaign by ID
 * @param {string} campaignId - The campaign ID
 * @returns {Promise<Object>} Newsletter campaign object
 */
export const getNewsletterCampaignById = async (campaignId) => {
  try {
    if (!campaignId) {
      throw new Error('Campaign ID is required');
    }

    const campaignRef = doc(db, NEWSLETTER_COLLECTION, campaignId);
    const campaignDoc = await getDoc(campaignRef);
    
    if (!campaignDoc.exists()) {
      throw new Error('Campaign not found');
    }

    return {
      id: campaignDoc.id,
      ...campaignDoc.data(),
      createdAt: campaignDoc.data().createdAt?.toDate() || null,
      updatedAt: campaignDoc.data().updatedAt?.toDate() || null
    };
  } catch (error) {
    console.error('Error getting newsletter campaign by ID:', error);
    throw error;
  }
};

/**
 * Get user newsletter data (same as individual newsletter page)
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} User newsletter data object
 */
export const getUserNewsletterData = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    return {
      campaigns: userData.newsletterCampaigns || [],
      lastUpdated: userData.newsletterLastUpdated || null
    };
  } catch (error) {
    console.error('Error getting user newsletter data:', error);
    throw error;
  }
};

/**
 * Save user newsletter data (same as individual newsletter page)
 * @param {string} userId - The user's UID
 * @param {Object} data - Newsletter data to save
 * @returns {Promise<void>}
 */
export const saveUserNewsletterData = async (userId, data) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      newsletterCampaigns: data.campaigns || [],
      newsletterLastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error saving user newsletter data:', error);
    throw error;
  }
};

// ===== USER-SPECIFIC NEWSLETTER DATA FUNCTIONS =====
// These functions save newsletter data directly to the user's document in the users collection

/**
 * Add a newsletter campaign to user's data
 * @param {string} userId - The user's UID
 * @param {Object} campaignData - Campaign data object
 * @returns {Promise<Object>} Created campaign object
 */
export const addUserNewsletterCampaign = async (userId, campaignData) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only add campaigns for their own account');
    }

    // Get current user data
    const currentData = await getUserNewsletterData(userId);
    const campaigns = currentData.campaigns || [];

    // Create new campaign
    const newCampaign = {
      id: Date.now().toString(),
      ...campaignData,
      platform: campaignData.platform || 'Not Specified',
      type: campaignData.type || 'General',
      totalEmails: parseInt(campaignData.totalEmails) || 0,
      subscribers: campaignData.subscribers || 0,
      sent: campaignData.sent || 0,
      metrics: {
        openRate: 0,
        clickRate: 0,
        bounceRate: 0,
        unsubscribeRate: 0,
        revenue: 0,
        ...campaignData.metrics
      }
    };

    // Add to campaigns array
    const updatedCampaigns = [...campaigns, newCampaign];

    // Save updated data
    await saveUserNewsletterData(userId, {
      campaigns: updatedCampaigns
    });

    return newCampaign;
  } catch (error) {
    console.error('Error adding user newsletter campaign:', error);
    throw error;
  }
};

/**
 * Update a newsletter campaign in user's data
 * @param {string} userId - The user's UID
 * @param {string} campaignId - The campaign ID
 * @param {Object} updateData - Updated campaign data
 * @returns {Promise<Object>} Updated campaign object
 */
export const updateUserNewsletterCampaign = async (userId, campaignId, updateData) => {
  try {
    if (!userId || !campaignId) {
      throw new Error('User ID and Campaign ID are required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only update their own campaigns');
    }

    // Get current user data
    const currentData = await getUserNewsletterData(userId);
    const campaigns = currentData.campaigns || [];

    // Find and update the campaign
    const updatedCampaigns = campaigns.map(campaign => 
      campaign.id === campaignId 
        ? { ...campaign, ...updateData }
        : campaign
    );

    // Check if campaign was found
    const campaignExists = campaigns.some(campaign => campaign.id === campaignId);
    if (!campaignExists) {
      throw new Error('Campaign not found');
    }

    // Save updated data
    await saveUserNewsletterData(userId, {
      campaigns: updatedCampaigns
    });

    return updatedCampaigns.find(campaign => campaign.id === campaignId);
  } catch (error) {
    console.error('Error updating user newsletter campaign:', error);
    throw error;
  }
};

/**
 * Delete a newsletter campaign from user's data
 * @param {string} userId - The user's UID
 * @param {string} campaignId - The campaign ID
 * @returns {Promise<void>}
 */
export const deleteUserNewsletterCampaign = async (userId, campaignId) => {
  try {
    if (!userId || !campaignId) {
      throw new Error('User ID and Campaign ID are required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only delete their own campaigns');
    }

    // Get current user data
    const currentData = await getUserNewsletterData(userId);
    const campaigns = currentData.campaigns || [];

    // Filter out the campaign to delete
    const updatedCampaigns = campaigns.filter(campaign => campaign.id !== campaignId);

    // Check if campaign was found
    if (updatedCampaigns.length === campaigns.length) {
      throw new Error('Campaign not found');
    }

    // Save updated data
    await saveUserNewsletterData(userId, {
      campaigns: updatedCampaigns
    });
  } catch (error) {
    console.error('Error deleting user newsletter campaign:', error);
    throw error;
  }
};

/**
 * Save newsletter management request data for a specific user
 * @param {string} userId - The user's UID
 * @param {Object} managementRequestData - Management request data object
 * @returns {Promise<Object>} Updated newsletter data
 */
export const saveNewsletterManagementRequest = async (userId, managementRequestData) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only save their own data');
    }

    // Get user data for display name, email, and company
    const userDoc = await getDoc(doc(db, 'users', userId));
    let requestedByName = 'Unknown User';
    let userEmail = 'unknown@example.com';
    let companyName = 'Unknown Company';
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const fullName = userData.displayName || 
                      (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
                      userData.email || 
                      'Unknown User';
      
      companyName = userData.company || 'Unknown Company';
      
      // Format as "Name" with "Company" underneath
      requestedByName = `${fullName}\n${companyName}`;
      userEmail = userData.email || 'unknown@example.com';
    } else {
      // If userId doesn't work, try to find user by email from auth
      const auth = getAuth();
      if (auth.currentUser && auth.currentUser.email) {
        try {
          const usersQuery = query(
            collection(db, 'users'),
            where('email', '==', auth.currentUser.email)
          );
          const usersSnapshot = await getDocs(usersQuery);
          
          if (!usersSnapshot.empty) {
            const userData = usersSnapshot.docs[0].data();
            const fullName = userData.displayName || 
                            (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
                            userData.email || 
                            'Unknown User';
            
            companyName = userData.company || 'Unknown Company';
            
            // Format as "Name" with "Company" underneath
            requestedByName = `${fullName}\n${companyName}`;
            userEmail = userData.email || 'unknown@example.com';
          }
        } catch (error) {
          console.error('Error finding user by email:', error);
        }
      }
    }

    // Get current newsletter data (if any exists)
    const currentData = await getUserNewsletterData(userId);
    
    // Create management request object for newsletter data
    const managementRequest = {
      totalSubscribers: Number(managementRequestData.totalSubscribers) || 0,
      newslettersPerMonth: Number(managementRequestData.newslettersPerMonth) || 0,
      submittedAt: new Date().toISOString(),
      status: 'pending'
    };

    // Create management request object for marketingRequests collection
    const marketingRequest = {
      userId: userId,
      requestedByName: requestedByName,
      userEmail: userEmail,
      title: 'Newsletter Management Request',
      description: `Newsletter management request for ${managementRequestData.totalSubscribers || 0} subscribers`,
      category: 'newsletter',
      priority: 'normal',
      budget: 'Not specified',
      status: 'pending',
      totalSubscribers: managementRequestData.totalSubscribers || 0,
      newslettersPerMonth: managementRequestData.newslettersPerMonth || 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Update the newsletter data with management request
    const updatedData = {
      ...currentData,
      managementRequest: managementRequest
    };

    // Save to newsletter database (create a newsletter collection similar to social media)
    const userDocRef = doc(db, 'newsletterData', userId);
    await setDoc(userDocRef, {
      campaigns: updatedData.campaigns || [],
      managementRequest: managementRequest,
      updatedAt: serverTimestamp(),
      createdAt: currentData.createdAt || serverTimestamp()
    });

    // Save to marketingRequests collection
    const requestsRef = collection(db, 'marketingRequests');
    const docRef = await addDoc(requestsRef, marketingRequest);
    
    console.log('✅ Newsletter management request saved:', docRef.id);

    // Trigger marketing fulfillment notification for newsletter management request
    try {
      const { triggerFulfillmentNotification } = await import('./notificationTriggerService');
      await triggerFulfillmentNotification('marketing', {
        type: 'newsletter management',
        name: 'Newsletter Management Request',
        description: `Newsletter management request for ${managementRequestData.totalSubscribers || 0} subscribers`,
        requestedBy: requestedByName,
        company: companyName,
        totalSubscribers: managementRequestData.totalSubscribers || 0,
        newslettersPerMonth: managementRequestData.newslettersPerMonth || 0,
        timestamp: new Date().toISOString()
      });
      console.log('✅ Marketing fulfillment notification sent for newsletter management request');
    } catch (notificationError) {
      console.error('❌ Error sending marketing fulfillment notification:', notificationError);
      // Don't fail the request if notification fails
    }

    return updatedData;
  } catch (error) {
    console.error('Error saving newsletter management request:', error);
    throw error;
  }
};

/**
 * Get user newsletter data including management request
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} User newsletter data
 */
export const getUserNewsletterDataWithManagement = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only access their own data');
    }

    const userDocRef = doc(db, 'newsletterData', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      return userDoc.data();
    } else {
      // Fallback to the original getUserNewsletterData if newsletterData doesn't exist
      const originalData = await getUserNewsletterData(userId);
      return {
        ...originalData,
        managementRequest: null,
        createdAt: null
      };
    }
  } catch (error) {
    console.error('Error getting user newsletter data:', error);
    throw error;
  }
};

/**
 * Cancel newsletter management request for a specific user
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} Updated newsletter data
 */
export const cancelNewsletterManagementRequest = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only save their own data');
    }

    // Get current data
    const currentData = await getUserNewsletterDataWithManagement(userId);
    
    // Remove management request
    const updatedData = {
      ...currentData,
      managementRequest: null
    };

    // Save to newsletter database
    const userDocRef = doc(db, 'newsletterData', userId);
    await setDoc(userDocRef, {
      campaigns: updatedData.campaigns || [],
      managementRequest: null,
      updatedAt: serverTimestamp(),
      createdAt: currentData.createdAt || serverTimestamp()
    });

    // Remove from marketingRequests collection
    try {
      const requestsQuery = query(
        collection(db, 'marketingRequests'),
        where('userId', '==', userId),
        where('category', '==', 'newsletter'),
        where('status', '==', 'pending')
      );
      const requestsSnapshot = await getDocs(requestsQuery);
      
      // Delete all pending newsletter requests for this user
      const deletePromises = requestsSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      console.log('✅ Cancelled newsletter management requests:', requestsSnapshot.docs.length);
    } catch (error) {
      console.error('Error removing from marketingRequests:', error);
      // Don't throw here, as the main operation succeeded
    }

    return updatedData;
  } catch (error) {
    console.error('Error canceling newsletter management request:', error);
    throw error;
  }
};

// ===== SHARED NEWSLETTER DATA FUNCTIONS FOR INTERNAL USERS =====
// These functions work with shared newsletter data that all internal users can access

const SHARED_NEWSLETTER_USER_ID = 'shared';

/**
 * Get shared newsletter data for all internal users
 * @returns {Promise<Object>} Shared newsletter data object
 */
export const getSharedNewsletterData = async () => {
  try {
    const userRef = doc(db, 'newsletterData', SHARED_NEWSLETTER_USER_ID);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // Return empty data structure if document doesn't exist
      return {
        campaigns: [],
        lastUpdated: null
      };
    }

    const userData = userDoc.data();
    return {
      campaigns: userData.campaigns || [],
      lastUpdated: userData.updatedAt?.toDate() || userData.lastUpdated || null
    };
  } catch (error) {
    console.error('Error getting shared newsletter data:', error);
    throw error;
  }
};

/**
 * Save shared newsletter data for all internal users
 * @param {Object} data - Newsletter data to save
 * @returns {Promise<void>}
 */
export const saveSharedNewsletterData = async (data) => {
  try {
    const userRef = doc(db, 'newsletterData', SHARED_NEWSLETTER_USER_ID);
    await setDoc(userRef, {
      campaigns: data.campaigns || [],
      updatedAt: serverTimestamp(),
      lastUpdated: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving shared newsletter data:', error);
    throw error;
  }
};

/**
 * Add a newsletter campaign to shared data
 * @param {Object} campaignData - Campaign data object
 * @returns {Promise<Object>} Created campaign object
 */
export const addSharedNewsletterCampaign = async (campaignData) => {
  try {
    // Get current shared data
    const currentData = await getSharedNewsletterData();
    const campaigns = currentData.campaigns || [];

    // Create new campaign
    const newCampaign = {
      id: Date.now().toString(),
      ...campaignData,
      platform: campaignData.platform || 'Not Specified',
      type: campaignData.type || 'General',
      totalEmails: parseInt(campaignData.totalEmails) || 0,
      subscribers: campaignData.subscribers || 0,
      sent: campaignData.sent || 0,
      metrics: {
        openRate: 0,
        clickRate: 0,
        bounceRate: 0,
        unsubscribeRate: 0,
        revenue: 0,
        ...campaignData.metrics
      }
    };

    // Add to campaigns array
    const updatedCampaigns = [...campaigns, newCampaign];

    // Save updated data
    await saveSharedNewsletterData({
      campaigns: updatedCampaigns
    });

    return newCampaign;
  } catch (error) {
    console.error('Error adding shared newsletter campaign:', error);
    throw error;
  }
};

/**
 * Update a newsletter campaign in shared data
 * @param {string} campaignId - The campaign ID
 * @param {Object} updateData - Updated campaign data
 * @returns {Promise<Object>} Updated campaign object
 */
export const updateSharedNewsletterCampaign = async (campaignId, updateData) => {
  try {
    if (!campaignId) {
      throw new Error('Campaign ID is required');
    }

    // Get current shared data
    const currentData = await getSharedNewsletterData();
    const campaigns = currentData.campaigns || [];

    // Find and update the campaign
    const updatedCampaigns = campaigns.map(campaign => 
      campaign.id === campaignId 
        ? { ...campaign, ...updateData }
        : campaign
    );

    // Check if campaign was found
    const campaignExists = campaigns.some(campaign => campaign.id === campaignId);
    if (!campaignExists) {
      throw new Error('Campaign not found');
    }

    // Save updated data
    await saveSharedNewsletterData({
      campaigns: updatedCampaigns
    });

    return updatedCampaigns.find(campaign => campaign.id === campaignId);
  } catch (error) {
    console.error('Error updating shared newsletter campaign:', error);
    throw error;
  }
};

/**
 * Delete a newsletter campaign from shared data
 * @param {string} campaignId - The campaign ID
 * @returns {Promise<void>}
 */
export const deleteSharedNewsletterCampaign = async (campaignId) => {
  try {
    if (!campaignId) {
      throw new Error('Campaign ID is required');
    }

    // Get current shared data
    const currentData = await getSharedNewsletterData();
    const campaigns = currentData.campaigns || [];

    // Filter out the campaign to delete
    const updatedCampaigns = campaigns.filter(campaign => campaign.id !== campaignId);

    // Check if campaign was found
    if (updatedCampaigns.length === campaigns.length) {
      throw new Error('Campaign not found');
    }

    // Save updated data
    await saveSharedNewsletterData({
      campaigns: updatedCampaigns
    });
  } catch (error) {
    console.error('Error deleting shared newsletter campaign:', error);
    throw error;
  }
};

// ===== SHARED MATERIAL FUNCTIONS FOR INTERNAL USERS =====
// These functions work with shared material data that all internal users can access

/**
 * Get shared materials data for all internal users
 * @returns {Promise<Array>} Array of material objects
 */
export const getSharedMaterials = async () => {
  try {
    const userRef = doc(db, 'newsletterData', SHARED_NEWSLETTER_USER_ID);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return [];
    }

    const userData = userDoc.data();
    return userData.materials || [];
  } catch (error) {
    console.error('Error getting shared materials:', error);
    throw error;
  }
};

/**
 * Add a material to shared data
 * @param {Object} materialData - Material data object
 * @returns {Promise<Object>} Created material object
 */
export const addSharedMaterial = async (materialData) => {
  try {
    const currentMaterials = await getSharedMaterials();
    
    const newMaterial = {
      id: Date.now().toString(),
      ...materialData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const updatedMaterials = [...currentMaterials, newMaterial];

    const userRef = doc(db, 'newsletterData', SHARED_NEWSLETTER_USER_ID);
    await setDoc(userRef, {
      materials: updatedMaterials,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return newMaterial;
  } catch (error) {
    console.error('Error adding shared material:', error);
    throw error;
  }
};

/**
 * Update a material in shared data
 * @param {string} materialId - The material ID
 * @param {Object} updateData - Updated material data
 * @returns {Promise<Object>} Updated material object
 */
export const updateSharedMaterial = async (materialId, updateData) => {
  try {
    if (!materialId) {
      throw new Error('Material ID is required');
    }

    const currentMaterials = await getSharedMaterials();
    
    const updatedMaterials = currentMaterials.map(material => 
      material.id === materialId 
        ? { ...material, ...updateData, updatedAt: new Date().toISOString() }
        : material
    );

    const materialExists = currentMaterials.some(material => material.id === materialId);
    if (!materialExists) {
      throw new Error('Material not found');
    }

    const userRef = doc(db, 'newsletterData', SHARED_NEWSLETTER_USER_ID);
    await setDoc(userRef, {
      materials: updatedMaterials,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return updatedMaterials.find(material => material.id === materialId);
  } catch (error) {
    console.error('Error updating shared material:', error);
    throw error;
  }
};

/**
 * Delete a material from shared data
 * @param {string} materialId - The material ID
 * @returns {Promise<void>}
 */
export const deleteSharedMaterial = async (materialId) => {
  try {
    if (!materialId) {
      throw new Error('Material ID is required');
    }

    const currentMaterials = await getSharedMaterials();
    const updatedMaterials = currentMaterials.filter(material => material.id !== materialId);

    if (updatedMaterials.length === currentMaterials.length) {
      throw new Error('Material not found');
    }

    const userRef = doc(db, 'newsletterData', SHARED_NEWSLETTER_USER_ID);
    await setDoc(userRef, {
      materials: updatedMaterials,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error deleting shared material:', error);
    throw error;
  }
};